import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { Font, pdf } from '@react-pdf/renderer';
import { Document } from '@react-pdf/renderer';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { FOR_BANK_SECTIONS } from 'constants/businessToolSections/forBank';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { PdfViewer } from 'PDF/components/PDFViewer/PDFViewer';
import { PrivateSectionsEnum } from 'PDF/pages/TableOfContents/helpers';
import { SectionsAvailability, SectionsType } from 'PDF/types/pdfSections';
import { INDUSTRY_TYPE } from 'services/businessPlan/types';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getActiveSection,
  getBusinessPlan,
  getBusinessPlanChapters,
  getSetBusinessPlan,
} from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useExamplePreview } from 'hooks/useExamplePreview';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { OTHER_SERVICES_SECTOR } from 'components/BusinessToolForm/constants/industrySectorOptions';
import { useGetOperatingExpensesTableHeadings } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/OperatingExpensesForm/utils/form';
import {
  useGetCurrentAssetsVariants,
  useGetNonCurrentAssetsVariants,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/Assets/utils/form';
import { useGetLiabilityAndEquityTableHeadings } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedBalanceSheets/Sections/LiabilitiesAndEquity/utils/form';
import { useGetAdjustmentsForLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/AdjustmentsForForm/utils/form';
import { useGetOperatingAssetsAndLiabilitiesLinkedFields } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OperatingAssetsAndLiabilitiesForm/utils/form';
import { useGetOperatingActivitiesRows } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { GLOBAL_FIELD_NAMES } from 'components/BusinessToolForm/Sections/Global/form';
import { theme } from 'components/Theme/theme';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { pdfWordSplitter } from 'helpers/pdfWordSplitter';
import ToastService from 'helpers/ToastService';

import { PreviewSectionLoader } from './PreviewSectionLoader';
import { getPreviewSectionsAvailability, getSectionKey, getSectionPdf } from './utils';
import { TableOfContents } from '../../../PDF/pages/TableOfContents/TableOfContents';
import { businessPlanQueryKeys } from '../../../services/businessPlan/businessPlan-qk';
import { BusinessPlanApi } from '../../../services/businessPlan/businessPlan.api';

Font.registerHyphenationCallback(pdfWordSplitter);

export const PdfPreview: FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const { isExamplePage} = useExamplePreview()
  const businessPlan = useBusinessToolStore(getBusinessPlan);
  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
  const activeSection = useBusinessToolStore(getActiveSection);
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const companySector = businessPlanChapters.GLOBAL[GLOBAL_FIELD_NAMES.COMPANY_SECTOR];
  const sector =
    typeof companySector === 'object' && companySector !== null ? companySector.value : OTHER_SERVICES_SECTOR;
  const { data: industryPictures } = useQuery({
    queryKey: businessPlanQueryKeys.businessPlanIndustryPictures(INDUSTRY_TYPE[sector]),
    queryFn: () => BusinessPlanApi.getIndustryPictutes({ type: INDUSTRY_TYPE[sector] }),
    enabled: true,
    placeholderData: keepPreviousData,
  });
  const currentSection = getSectionKey(activeSection);
  const selectedColor = businessPlan.metadata.documentColorType;
  const ref = useRef({ currentSection, selectedColor });
  const isInvestors = businessPlan.type === BUSINESS_PLAN_TYPES.FOR_INVESTORS;

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const canAccessProductAndServices = useCheckSubSectionAccess(FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES) && !isInvestors;
  const canAccessMarketAnalysis = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKET_ANALYSIS);
  const canAccessFinancialPlan = useCheckSubSectionAccess(FOR_BANK_SECTIONS.FINANCIAL_PLAN);
  const canAccessMarketingAndSales = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MARKETING_AND_SALES);
  const canAccessBusinessOperations = useCheckSubSectionAccess(FOR_BANK_SECTIONS.BUSINESS_OPERATIONS);
  const canAccessManagementAndOrganization = useCheckSubSectionAccess(FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION);
  const canAccessLegalFrameworkAndRisks =
    useCheckSubSectionAccess(FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS) && !isInvestors;
  const canAccessImplementationPlan = useCheckSubSectionAccess(FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN) && !isInvestors;
  const canAccessRaisingAndAllocatingFunds = useCheckSubSectionAccess(FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS);

  const canAccessMarketAnalysisInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS);
  const canAccessMarketingAndSalesInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.MARKETING_AND_SALES);
  const canAccessBusinessOperationsInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.BUSINESS_OPERATIONS);
  const canAccessManagementAndOrganizationInvestor = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
  );
  const canAccessFinancialPlanInvestor = useCheckSubSectionAccess(FOR_INVESTORS_SECTIONS.FINANCIAL_PLAN);
  const canAccessRaisingAndAllocatingFundsInvestor = useCheckSubSectionAccess(
    FOR_INVESTORS_SECTIONS.RAISING_AND_ALLOCATING_FUNDS,
  );

  const currentAssetsVariants = useGetCurrentAssetsVariants(isExamplePage);
  const nonCurrentAssetsVariants = useGetNonCurrentAssetsVariants(isExamplePage);
  const { currentLiabilitiesVariants, nonCurrentLiabilitiesVariants, equityVariants } =
    useGetLiabilityAndEquityTableHeadings(isExamplePage);
  const { operatingExpensesLinkedFields } = useGetOperatingExpensesTableHeadings(isExamplePage);
  const adjustmentsForLinkedFieldsValue = useGetAdjustmentsForLinkedFields(isExamplePage);
  const operatingAssetsAndLiabilitiesLinkedFieldsValue = useGetOperatingAssetsAndLiabilitiesLinkedFields(isExamplePage);
  const operatingActivitiesRowsValues = useGetOperatingActivitiesRows(isExamplePage)
  

  const privateSections = {
    [PrivateSectionsEnum.PRODUCT_AND_SERVICES]: canAccessProductAndServices,
    [PrivateSectionsEnum.MARKET_ANALYSIS]: canAccessMarketAnalysis || canAccessMarketAnalysisInvestor,
    [PrivateSectionsEnum.FINANCIAL_PLAN]: canAccessFinancialPlan || canAccessFinancialPlanInvestor,
    [PrivateSectionsEnum.BUSINESS_OPERATIONS]: canAccessBusinessOperations || canAccessBusinessOperationsInvestor,
    [PrivateSectionsEnum.MARKETING_AND_SALES]: canAccessMarketingAndSales || canAccessMarketingAndSalesInvestor,
    [PrivateSectionsEnum.LEGAL_FRAMEWORK_AND_RISKS]: canAccessLegalFrameworkAndRisks,
    [PrivateSectionsEnum.IMPLEMENTATION_PLAN]: canAccessImplementationPlan,
    [PrivateSectionsEnum.MANAGEMENT_AND_ORGANIZATION]:
      canAccessManagementAndOrganization || canAccessManagementAndOrganizationInvestor,
    [PrivateSectionsEnum.RAISING_AND_ALLOCATING_FUNDS]:
      canAccessRaisingAndAllocatingFunds || canAccessRaisingAndAllocatingFundsInvestor,
  };

  const initialSections: SectionsType = {
    coverPage: null,
    summary: undefined,
    summaryInvestor: undefined,
    aboutCompany: undefined,
    aboutCompanyInvestor: undefined,
    productAndServices: undefined,
    marketAnalysis: undefined,
    marketAnalysisInvestor: undefined,
    businessOperations: undefined,
    businessOperationsInvestor: undefined,
    managementAndOrganization: undefined,
    managementAndOrganizationInvestor: undefined,
    legalFrameworkAndRisks: undefined,
    implementationPlan: undefined,
    financialPlan: undefined,
    financialPlanInvestor: undefined,
    marketingAndSales: undefined,
    marketingAndSalesInvestor: undefined,
    raisingAndAllocatingFunds: undefined,
    raisingAndAllocatingFundsInvestor: undefined,
    lastPage: null,
  };

  if (businessPlan.type === BUSINESS_PLAN_TYPES.FOR_INVESTORS) {
    initialSections.summaryInvestor = null;
    initialSections.aboutCompanyInvestor = null;
    initialSections.marketAnalysisInvestor = canAccessMarketAnalysisInvestor ? null : undefined;
    initialSections.marketingAndSalesInvestor = canAccessMarketingAndSalesInvestor ? null : undefined;
    initialSections.businessOperationsInvestor = canAccessBusinessOperationsInvestor ? null : undefined;
    initialSections.managementAndOrganizationInvestor = canAccessManagementAndOrganizationInvestor ? null : undefined;
    initialSections.raisingAndAllocatingFundsInvestor = canAccessRaisingAndAllocatingFundsInvestor ? null : undefined;
    initialSections.financialPlanInvestor = canAccessFinancialPlanInvestor ? null : undefined;
  } else {
    initialSections.summary = null;
    initialSections.aboutCompany = null;
    initialSections.productAndServices = canAccessProductAndServices ? null : undefined;
    initialSections.marketAnalysis = canAccessMarketAnalysis ? null : undefined;
    initialSections.marketingAndSales = canAccessMarketingAndSales ? null : undefined;
    initialSections.businessOperations = canAccessBusinessOperations ? null : undefined;
    initialSections.managementAndOrganization = canAccessManagementAndOrganization ? null : undefined;
    ((initialSections.implementationPlan = canAccessImplementationPlan ? null : undefined),
      (initialSections.legalFrameworkAndRisks = canAccessLegalFrameworkAndRisks ? null : undefined),
      (initialSections.raisingAndAllocatingFunds = canAccessRaisingAndAllocatingFunds ? null : undefined));
    initialSections.financialPlan = canAccessFinancialPlan ? null : undefined;
  }

  const [sections, setSections] = useState<SectionsType>(initialSections);

  const handleSetSectionsProps = useCallback((key: string, props: Record<string, any>) => {
    setSections((prevState) => ({ ...prevState, [key]: props }));
  }, []);

  const handlePdfLoaded = useCallback(() => {
    setLoading(false);
  }, []);

  // openingBalanceSheetData is needed for calculating the possibility of rendering the Project Balance Sheet
  const openingBalanceSheetData = currentSection.includes('financialPlan')
    ? businessPlanChapters.FINANCIAL_PLAN.openingBalanceSheets
    : undefined;

  useEffect(() => {
    const generatePdf = async (sectionsAvailability: SectionsAvailability) => {
      try {
        const pdfBlob = await pdf(
          <Document>
            {getSectionPdf(
              currentSection,
              sections,
              businessPlan.type,
              industryPictures,
              privateSections,
              sectionsAvailability,
              isStandardOrLightTariff,
              isExamplePage,
              openingBalanceSheetData,
            )}
          </Document>,
        ).toBlob();
        const url = URL.createObjectURL(pdfBlob);
        setBusinessPlan({
          ...businessPlan,
          metadata: { ...getValidatedMetadata(businessPlan.metadata), pdfDocShortLink: url },
        });
        setPdfUrl((prevUrl) => url || prevUrl);
      } catch {
        ToastService.showError('PDF generation error');
        setLoading(false);
      }
    };

    if (sections[`${currentSection}`] !== null && industryPictures) {
      const sectionsAvailabilityPreview = getPreviewSectionsAvailability(
        businessPlanChapters,
        businessPlan.type,
        privateSections,
        currentAssetsVariants,
        nonCurrentAssetsVariants,
        currentLiabilitiesVariants,
        nonCurrentLiabilitiesVariants,
        equityVariants,
        operatingExpensesLinkedFields,
        adjustmentsForLinkedFieldsValue,
        operatingAssetsAndLiabilitiesLinkedFieldsValue,
        operatingActivitiesRowsValues,
        isExamplePage
      );

      generatePdf(sectionsAvailabilityPreview);
    }
  }, [sections, industryPictures, currentSection]);

  const isSectionEmpty = !sections[currentSection];

  useEffect(() => {
    const shouldResetPreview =
      currentSection !== ref.current.currentSection ||
      selectedColor !== ref.current.selectedColor;

    if (!loading && shouldResetPreview) {
      setLoading(true);
      setPdfUrl('');
      setSections(initialSections);
      ref.current = { currentSection, selectedColor };
    }
  }, [currentSection, selectedColor, businessPlan.uuid, loading]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
      </ThemeProvider>
      {!isSectionEmpty ? (
        <PreviewSectionLoader loading={currentSection !== ref.current.currentSection || loading} />
      ) : (
        <PreviewSectionLoader loading={pdfUrl === ''} />
      )}
      {loading && Object.values(sections).includes(null) && (
        <TableOfContents saveSectionProps={handleSetSectionsProps} />
      )}
      {pdfUrl !== '' && <PdfViewer pdfUrl={pdfUrl} handlePdfLoaded={handlePdfLoaded} />}
    </>
  );
};
